"use client"
import { useState } from "react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { WorkflowGrid } from "@/components/workflow-grid"
import { FilterDrawer } from "@/components/filter-drawer"
import { ViewSelector } from "@/components/view-selector"
import { WorkerSearchModal } from "@/components/worker-search-modal"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Search } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import type { WorkflowStage } from "@/types/workflow"
import { useLocalizedField } from '@/utils/language-utils'
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import { useTranslation } from "@/hooks/use-translation"

export function WorkflowHub() {
  const { currentStage, setCurrentStage, isFilterDrawerOpen, stages } = useWorkflow()
  const getLocalizedField = useLocalizedField()
  const { trackStageChange } = useWorkflowTracking()
  const { t } = useTranslation()
  const [isWorkerSearchOpen, setIsWorkerSearchOpen] = useState(false)

  // Wait until stages and currentStage are set
  if (!stages.length || !currentStage) {
    return null; // Optionally, replace with a loading spinner
  }

  // Calculate the maximum width needed for all stage tabs
  const maxTabWidth = Math.max(...stages.map(stage => {
    const title = getLocalizedField(stage, 'title')
    // Estimate width based on character count (rough approximation)
    // Each character is roughly 10px + padding
    return title.length * 10 + 64 // 64px for padding (32px on each side)
  }))

  return (
    <div className="flex h-screen overflow-hidden workflow-app-container">
      <div className="flex flex-col w-full overflow-hidden">
        {/* Simple header with navigation breadcrumb */}
        {/* <FBOHeader /> */}

        <main className="flex-1 overflow-hidden bg-white flex flex-col">
          <div className="px-8 py-6 flex-grow flex flex-col overflow-hidden">
            <Tabs
              value={currentStage}
              onValueChange={(value) => {
                const newStage = value as WorkflowStage
                if (newStage !== currentStage) {
                  trackStageChange(currentStage, newStage)
                }
                setCurrentStage(newStage)
              }}
              className="stage-tabs w-full h-full flex flex-col overflow-hidden"
            >
              <div className="stage-tabs-list flex justify-between items-center mb-4">
                <TabsList
                  style={{
                    display: 'flex',
                    background: '#FAF5EF',
                    borderRadius: 8,
                    padding: '0 4px',
                    gap: 0,
                  }}
                >
                  {stages.map((stage) => (
                    <TabsTrigger
                      key={stage.stageCode}
                      value={stage.stageCode}
                      style={{
                        background: currentStage === stage.stageCode ? '#FFA96C' : 'transparent',
                        color: currentStage === stage.stageCode ? '#222' : '#6B6B6B',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 16,
                        fontWeight: currentStage === stage.stageCode ? 700 : 500,
                        padding: '10px 32px',
                        cursor: 'pointer',
                        transition: 'background 0.2s, color 0.2s',
                        width: maxTabWidth,
                        minWidth: maxTabWidth,
                        flex: '1 1 0',
                        textAlign: 'center',
                      }}
                    >
                      {getLocalizedField(stage, 'title')}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {/* View Selector and Search Button aligned with stage tabs */}
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setIsWorkerSearchOpen(true)}
                          className="h-9 w-9" 
                          aria-label={t('searchWorkers') || 'Search Workers'}
                        >
                          <Search size={18} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('searchWorkers') || 'Search Workers'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <ViewSelector />
                </div>
              </div>

              {/* Only render a single WorkflowGrid for the active stage */}
              <TabsContent key={currentStage} value={currentStage} className="flex-1 overflow-hidden">
                <WorkflowGrid stage={currentStage} />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>

      {isFilterDrawerOpen && <FilterDrawer />}
      <WorkerSearchModal isOpen={isWorkerSearchOpen} onClose={() => setIsWorkerSearchOpen(false)} />
    </div>
  )
}
